import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';

import Text from '~/components/core/TextComponents/Text';
import Divider from '~/components/ui/atoms/Divider';

import { ACTION_NEEDS_ATTENTION } from '../constants';
import { activityCardElementId } from '../utils';

import ActionCard from './ActionCard';
import KindChips from './KindChips';
import QuestionCard from './QuestionCard';
import RequestCard from './RequestCard';
import StatusFilterBar from './StatusFilter';
import type { ActivityRow, KindFilter, PaneArrival, StatusFilter } from './types';
import { attentionSummary, emptyStateMessage, filterRows } from './utils';

/** What a card needs from the panel rather than from its own row. */
interface CardContext {
  onActionUpdate: () => Promise<void>;
  onAskUpdate: () => Promise<void>;
  onSelectLogEntry: (row: ActivityRow) => void;
  /** Whether the log last took the reader to this card. */
  isSelected: boolean;
  onDeselect: () => void;
}

type CardRenderers = {
  [Kind in ActivityRow['kind']]: (
    row: Extract<ActivityRow, { kind: Kind }>,
    context: CardContext
  ) => React.ReactElement;
};

/** One card per kind of row, so a new kind is a new key rather than another branch. */
const CARD_RENDERERS: CardRenderers = {
  recommendation: (row, { onActionUpdate, onSelectLogEntry, isSelected, onDeselect }) => (
    <ActionCard
      row={row}
      onActionUpdate={onActionUpdate}
      onSelectLogEntry={onSelectLogEntry}
      isSelected={isSelected}
      onDeselect={onDeselect}
    />
  ),
  request: (row, { onAskUpdate, onSelectLogEntry, isSelected, onDeselect }) => (
    <RequestCard
      row={row}
      onAskUpdate={onAskUpdate}
      onSelectLogEntry={onSelectLogEntry}
      isSelected={isSelected}
      onDeselect={onDeselect}
    />
  ),
  question: (row, { onSelectLogEntry, isSelected, onDeselect }) => (
    <QuestionCard row={row} onSelectLogEntry={onSelectLogEntry} isSelected={isSelected} onDeselect={onDeselect} />
  ),
};

const renderCard = (row: ActivityRow, context: CardContext): React.ReactElement =>
  // As in the log's `renderEntry`: the record is keyed by the row's own discriminant, which
  // TypeScript cannot correlate through an index access.
  (CARD_RENDERERS[row.kind] as (member: ActivityRow, context: CardContext) => React.ReactElement)(row, context);

interface ActivityPanelProps {
  /**
   * Everything Clive has raised on the claim, already resolved into rows. The builder resolves each
   * kind from its own sources, so nothing here knows which sources a kind needed for its state.
   */
  rows: ActivityRow[];
  /** Refetches the claim's actions after a gesture, which both panes resolve from. */
  onActionUpdate: () => Promise<void>;
  /** Refetches the claim's asks after a gesture, which both panes resolve from. */
  onAskUpdate: () => Promise<void>;
  /**
   * Brings a card's log entry into view and marks it selected. It takes the whole row because the log
   * may not have loaded the entry yet, and the row is what it is found by.
   */
  onSelectLogEntry: (row: ActivityRow) => void;
  /** The card the log last took the reader to, which the panel shows whatever its filters were. */
  arrival?: PaneArrival;
}

/**
 * Right panel of `conductor_hub`: what Clive has raised, one card per thing in the order raised,
 * headed by a count of what needs attention. Filtered two ways — by kind, and by status — and it
 * opens on what needs attention, because the question on opening is *what is on me*. A failed
 * execution counts as waiting, because nobody has been told the write was refused.
 *
 * One filtered list rather than tabs, so a blocking question cannot sit unseen behind a tab.
 *
 * Presentational: it takes the rows rather than reading them. The tab builds them from the same
 * sources the log reads, through the same resolvers, so a card cannot say one thing here and
 * another there.
 */
const ActivityPanel: React.FC<ActivityPanelProps> = ({
  rows,
  onActionUpdate,
  onAskUpdate,
  onSelectLogEntry,
  arrival,
}) => {
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('needsAttention');
  const [ring, setRing] = useState<PaneArrival>();

  // Arriving from the log relaxes both filters, so the card is listed whatever they were set to.
  useEffect(() => {
    if (!arrival) return;
    setKind('all');
    setStatus('all');
    setRing(arrival);
  }, [arrival]);

  // Its own effect, so it runs after the render the relaxed filters produce, once the card is listed.
  useEffect(() => {
    if (!ring) return;
    document.getElementById(activityCardElementId(ring.key))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [ring]);

  const visibleRows = filterRows(rows, kind, status);
  const summary = attentionSummary(rows.filter((row) => ACTION_NEEDS_ATTENTION[row.state]).length);

  return (
    <div className="flex h-full flex-col px-24 pt-20">
      <div className="flex shrink-0 flex-col gap-12">
        <div className="flex items-baseline gap-8">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            From Clive
          </Text>
          {/* Counted over every row, not the visible ones: the header answers "what is on me on
              this claim", not "what is on me in this filter". At zero it says nothing, because the
              empty state below already says it. */}
          {summary ? (
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {summary}
            </Text>
          ) : null}
        </div>

        <KindChips value={kind} onChange={setKind} />
        <StatusFilterBar value={status} onChange={setStatus} />
      </div>

      <Divider className="mb-16 mt-12 shrink-0" />

      {/* Only the cards scroll: the header and the filters are how the reader changes what the list
          holds, so they stay in reach. */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-20">
        {!isEmpty(visibleRows) ? (
          <ul aria-label="From Clive" className="m-0 flex list-none flex-col gap-12 p-0">
            {visibleRows.map((row) => (
              <React.Fragment key={row.key}>
                {renderCard(row, {
                  onActionUpdate,
                  onAskUpdate,
                  onSelectLogEntry,
                  isSelected: ring?.key === row.key,
                  // Conditional, as the log's deselect is: following the log to another card is also a
                  // click outside this one, which a browser delivers here after the arrival has moved
                  // the ring, and it must not undo the move.
                  onDeselect: () => setRing((current) => (current?.key === row.key ? undefined : current)),
                })}
              </React.Fragment>
            ))}
          </ul>
        ) : (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {emptyStateMessage(kind, status, !isEmpty(rows))}
          </Text>
        )}
      </div>
    </div>
  );
};

export default ActivityPanel;
